var _dec, _class, _PipButton;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
/** @jsx jsx */
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withStyle, jsx } from '@instructure/emotion';
import { View } from '@instructure/ui-view';
import { Responsive } from '@instructure/ui-responsive';
import { Tooltip } from '@instructure/ui-tooltip';
import VideoDeviceSelection from '../VideoDeviceSelection';
import AudioDeviceSelection from '../AudioDeviceSelection';
import ScreenSharingControl from '../ScreenSharingControl';
import CTA from '../CTA';
import ContinueOrPauseButton from '../ContinueOrPauseButton';
import PipButton from '../PipButton/PipButton';
import FileSave from '../FileSave';
import * as CaptureActions from '../../../actions';
import { STARTING, RECORDING, PREVIEWSAVE, SAVING, LOADING, PAUSED } from '../../../constants/CaptureStates';
import { translate } from '../../../constants/translated/translations';
import { canUseScreenCapture } from '../../../utils';
import generateStyle from './styles';
import { DeviceError } from '../../../constants/DeviceError';

/**
---
private: true
---
**/
let Controller = (_dec = withStyle(generateStyle), _dec(_class = class Controller extends Component {
  constructor(props) {
    super(props);
    this.showVideoDeviceTooltip = () => {
      const _this$props = this.props,
        videoDeviceSystemPermissionError = _this$props.videoDeviceSystemPermissionError,
        screenSharingVideoDeviceConflict = _this$props.screenSharingVideoDeviceConflict;
      if (screenSharingVideoDeviceConflict || videoDeviceSystemPermissionError) {
        this.setState({
          isVideoDeviceTooltipShown: true
        });
      }
    };
    this.hideVideoDeviceTooltip = () => {
      this.setState({
        isVideoDeviceTooltipShown: false
      });
    };
    this.showAudioDeviceTooltip = () => {
      if (this.props.audioDeviceSystemPermissionError) {
        this.setState({
          isAudioDeviceTooltipShown: true
        });
      }
    };
    this.hideAudioDeviceTooltip = () => {
      this.setState({
        isAudioDeviceTooltipShown: false
      });
    };
    this.continueOrPauseButtonRef = /*#__PURE__*/React.createRef();
    this.state = {
      isVideoDeviceTooltipShown: false,
      isAudioDeviceTooltipShown: false
    };
  }
  componentDidMount() {
    this.props.makeStyles();
  }
  componentDidUpdate() {
    this.props.makeStyles();
    if (this.continueOrPauseButtonRef.current) {
      this.continueOrPauseButtonRef.current.focus();
    }
  }
  isDisabled() {
    const captureState = this.props.captureState;
    return captureState === STARTING;
  }
  renderStart() {
    const _this$props2 = this.props,
      styles = _this$props2.styles,
      captureState = _this$props2.captureState,
      canRecord = _this$props2.canRecord,
      noScreenSharing = _this$props2.noScreenSharing,
      actions = _this$props2.actions,
      screenSharingBetaActionText = _this$props2.screenSharingBetaActionText,
      screenSharingBetaActionURL = _this$props2.screenSharingBetaActionURL,
      screenSharingVideoDeviceConflict = _this$props2.screenSharingVideoDeviceConflict,
      audioDeviceSystemPermissionError = _this$props2.audioDeviceSystemPermissionError,
      videoDeviceSystemPermissionError = _this$props2.videoDeviceSystemPermissionError;
    const shouldVideoBeDisabled = this.isDisabled() || screenSharingVideoDeviceConflict;
    const videoDeviceTooltipMessage = videoDeviceSystemPermissionError ? translate('NO_SYSTEM_VIDEO_PERMISSION_TOOLTIP') : translate('PICTURE_IN_PICTURE_TOOLTIP');
    const audioDeviceTooltipMessage = audioDeviceSystemPermissionError ? translate('NO_SYSTEM_AUDIO_PERMISSION_TOOLTIP') : '';
    return jsx(Responsive, {
      match: "media",
      query: {
        small: {
          maxWidth: 769
        }
      }
    }, (_, matches) => jsx(View, {
      as: "div",
      padding: matches.includes('small') ? 'medium' : 'small'
    }, jsx(View, {
      as: "div",
      css: styles.container
    }, ![RECORDING, PAUSED].includes(captureState) ? jsx(React.Fragment, null, jsx(Tooltip, {
      as: "div",
      renderTip: videoDeviceTooltipMessage,
      color: "primary-inverse",
      isShowingContent: this.state.isVideoDeviceTooltipShown,
      onShowContent: this.showVideoDeviceTooltip,
      onHideContent: this.hideVideoDeviceTooltip
    }, jsx(VideoDeviceSelection, {
      isDisabled: shouldVideoBeDisabled,
      mobile: matches.includes('small')
    })), jsx(Tooltip, {
      as: "div",
      id: "device-selection-label",
      renderTip: audioDeviceTooltipMessage,
      color: "primary-inverse",
      isShowingContent: this.state.isAudioDeviceTooltipShown,
      onShowContent: this.showAudioDeviceTooltip,
      onHideContent: this.hideAudioDeviceTooltip
    }, jsx(AudioDeviceSelection, {
      isDisabled: this.isDisabled(),
      mobile: matches.includes('small'),
      labelledBy: "device-selection-label"
    })), !noScreenSharing && canUseScreenCapture() ? jsx(View, {
      css: styles.screenSharingControlContainer
    }, jsx(ScreenSharingControl, {
      isDisabled: this.isDisabled(),
      betaActionText: screenSharingBetaActionText,
      betaActionURL: screenSharingBetaActionURL,
      mobile: matches.includes('small')
    })) : null) : jsx(React.Fragment, null, !videoDeviceSystemPermissionError ? jsx(View, {
      css: styles.pipButtonContainer
    }, _PipButton || (_PipButton = jsx(PipButton, null))) : null, jsx(View, {
      css: styles.pauseButtonContainer
    }, jsx(ContinueOrPauseButton, {
      captureState: captureState,
      actions: actions,
      ref: this.continueOrPauseButtonRef
    }))), jsx(View, {
      css: styles.ctaContainer
    }, jsx(CTA, {
      isDisabled: this.isDisabled() || !canRecord
    })))));
  }
  renderPreview() {
    const _this$props3 = this.props,
      captureState = _this$props3.captureState,
      fileName = _this$props3.fileName,
      actions = _this$props3.actions,
      onStartoverClicked = _this$props3.onStartoverClicked;
    return jsx(Responsive, {
      match: "media",
      query: {
        small: {
          maxWidth: 480
        }
      }
    }, (_, matches) => jsx(View, {
      as: "div",
      padding: matches.includes('small') ? 'medium' : 'small'
    }, jsx(FileSave, {
      disabled: captureState === SAVING,
      defaultFileName: fileName,
      onSaveClicked: actions.saveClicked,
      onStartoverClicked: () => {
        actions.startoverClicked();
        onStartoverClicked();
      }
    })));
  }
  render() {
    switch (this.props.captureState) {
      case PREVIEWSAVE:
      case SAVING:
        return this.renderPreview();
      case LOADING:
        return null;
      default:
        return this.renderStart();
    }
  }
}) || _class);
const mapStateToProps = state => ({
  captureState: state.captureState,
  pipEnabled: state.pipEnabled,
  screenSharingVideoDeviceConflict: state.screenSharingActive && state.screenSharingType !== 'monitor',
  videoDeviceSystemPermissionError: state.noSystemPermissionDevices.has(DeviceError.Webcam),
  audioDeviceSystemPermissionError: state.noSystemPermissionDevices.has(DeviceError.Mic),
  devices: state.devices,
  fileName: state.fileName,
  canRecord: state.audioDeviceId || state.videoDeviceId || state.screenSharingActive
});
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(CaptureActions, dispatch)
});
export default connect(mapStateToProps, mapDispatchToProps)(Controller);